// src/com/plan_pos/TransPlanPos.js
import React, { useState, useEffect, useRef } from 'react';
import { Box } from '@mui/system';
import CloudDownloadIcon from '@mui/icons-material/CloudDownload';
import { TopContainer, TopHeader, TopContent, ControlLine, NoDataBox } from '../../common/TopStyle';
import { useLazyQuery, useReactiveVar } from '@apollo/client';
import { transPlanListParamRepo, transPlanListRepo, userInfoRepo } from '../../value/AppModel';
import { useAlertSetter } from '../../api/app-hook';
import { LIST_TRANS_PLAN_BY_DATE } from '../trans_plan/TransPlanGql';
import { AppObject, AppStyle, AppWord } from '../../value/AppConst';
import IsValid from '../../value/IsValid';
import VzmDateField from '../../common/VzmDateField';
import ValueMisc from '../../value/ValueMisc';
import { Button } from '@mui/material';
import SearchBox from '../../common/SearchBox';
import TransPlanPosLine from './TransPlanPosLine';
import TransPlanPosView from './TransPlanPosView';

/**
 * 차량관제 리스트
 * @returns 
 */
export default function TransPlanPos() {
    const [itemSelected, setItemSelected] = useState({});
    const transPlanListData = useReactiveVar(transPlanListRepo);
    const listParam = useReactiveVar(transPlanListParamRepo);
    const [paramChanged, setParamChanged] = useState(false);
    const [records, setRecords] = useState([]);
    const [searchText, setSearchText] = useState('');
    const [sortInfo, setSortInfo] = useState({id: null, order: 0}); // -1 : descending, 0: no sorting, 1: ascending
    const addAlert = useAlertSetter();
    const runRef = useRef(false);

    // ##### Call GraphQL to get List #####
    const [getTransPlanList, responseList] = useLazyQuery(LIST_TRANS_PLAN_BY_DATE, {
        // 목록은 서버에서 이미 운송사, 점포, 홈플러스 등의 사용자 구분하여 출력.
        ...AppObject.NoCachedFetch,
        onCompleted: (data, option) => {onCompleteGetList(data, option)},
		onError: (error) => addAlert({title: "자량관제 리스트 조회 오류", message: [error.message]})
    });

    useEffect(()=>{
        if(!listParam.planDate) {
            const planDate = ValueMisc.getYMD();
            transPlanListParamRepo({planDate: planDate});
            runRef.current = true;
            getTransPlanList({variables:{planDate: planDate}});
        }
    }, [listParam]);
    
    useEffect(() => {
        let list;
        const txt = searchText ? searchText.trim().toLowerCase() : '';
        if(IsValid.nullString(txt)) list = transPlanListData.records;
        else {
            list = transPlanListData.records.filter((item) => {
                return (
                    item.dcName.toLowerCase().includes(txt) ||
                    (item.storeName && item.storeName.toLowerCase().includes(txt)) ||
                    (item.vehPlates && item.vehPlates.toLowerCase().includes(txt))
                );
            })
        }

        if(sortInfo.id && sortInfo.order !== 0) {
            const newList = [...list].sort((a, b) => {
                const orderBy = sortInfo.order < 0 ? -1 : 1; // -1: descending, 1: ascending
                if(a[sortInfo.id] < b[sortInfo.id]) return -1 * orderBy;
                if(a[sortInfo.id] > b[sortInfo.id]) return orderBy;
                return 0;
            });
            list = newList;
        }

        setRecords(list);
    }, [searchText, transPlanListData, sortInfo]);

    // >>>>>>>>> callbacks <<<<<<<<<<<<<
    const onCompleteGetList = (data, clientOption) => {
        if(data.transPlanListByDate) {
            const newData = {...transPlanListData}; // filter 때문에. filter:{storeId, comId}
            newData.when = new Date().getTime()/1000;
            newData.records = data.transPlanListByDate;
            transPlanListRepo(newData);
            setParamChanged(false);

            /* if(!sessionInfo.user.isTransCompanyUser()) {
                // comId, custId 다른 경우 지도에 표시하지 않으므로 지도 넘어갈 때 지도에 나타나는 것을 찾아 갈 수 있게 미리 준비.
                // 운송사는 하지 않아도 됨.
                const dup = {};
                const mapVeh = {};
                const posMap = {}; // 이름과 위치로 같은 차량임을 판단.
                const listHavingPosition = data.transPlanListByDate.filter((v) => v.lat > MapValue.Korea.South && v.lat < MapValue.Korea.North && v.lon > MapValue.Korea.West && v.lon < MapValue.Korea.East);
                listHavingPosition.forEach((v) => {
                    if(v.comId === v.custId) {
                        mapVeh[v.vehInfoSeq] = v;
                        posMap[`${v.vehPlates}-${v.lat}-${v.lon}`] = v.vehInfoSeq;
                    }
                });
                // again to find duplicate
                listHavingPosition.forEach((v) =>{
                    if(v.comId !== v.custId) {
                        const key = `${v.vehPlates}-${v.lat}-${v.lon}`;
                        if(posMap[key]) {
                            dup[v.vehInfoSeq] = posMap[key];
                        }
                    }
                });
                duplicateRef.current = dup;
                mapVehRef.current = mapVeh;
            } */
        }
    };

    const onChangeDate = (id, value) => {
        if(value) {
            transPlanListParamRepo({planDate: value});
            setParamChanged(true);
        }
    };

    const onChangeSearchText = (text) => {
        setSearchText(text);
    };

    const onClickGetList = () => {
        if(listParam.planDate) {
            getTransPlanList({variables:{planDate: listParam.planDate}});
        }
    };

    const onClickSorter = (id, sortState) => {
        setSortInfo({id, order: sortState});
    };

    const loadingAny = responseList.loading;

    return(
        <TopContainer>
            <TopHeader>
                차량관제 리스트
            </TopHeader>
            <ControlLine>
                <VzmDateField
                    dict={{
                        id: 'planDate',
                        label: '도착일자',
                    }}
                    value={listParam.planDate || ''}
                    error={!IsValid.date(listParam.planDate)}
                    onChange={onChangeDate}
                />
                <Button
                    variant={AppStyle.mui.contained}
                    color={AppStyle.mui.primary}
                    size={AppWord.SMALL}
                    startIcon={<CloudDownloadIcon />}
                    style={{marginLeft: 10}}
                    onClick={onClickGetList}
                    disabled={loadingAny || (!paramChanged && transPlanListData.records.length > 0)}
                >
                    조회
                </Button>
            </ControlLine>
            <ControlLine>
                <SearchBox
                    value={searchText}
                    onChangeText={onChangeSearchText}
                    disabled={responseList.loading}
                />
            </ControlLine>
            <TransPlanPosLine
                onClickSorter={onClickSorter}
                idSorted={sortInfo.id}
            />
            <TopContent>
                {
                    records.length === 0 ?
                    <NoDataBox />
                    :
                    <Box>
                        {
                            records.map((item, index) => {
                                if(item.vehInfoSeq === itemSelected.vehInfoSeq) {
                                    return (
                                        <TransPlanPosView
                                            key={index}
                                            item={item}
                                            onClose={() => setItemSelected({})}
                                        />
                                    )
                                }
                                return (
                                    <TransPlanPosLine
                                        key={index}
                                        item={item}
                                        index={index}
                                        itemSelected={itemSelected}
                                        onClick={(item) => {setItemSelected(item)}}
                                    />
                                )
                            })
                        }
                    </Box>
                }
            </TopContent>
        </TopContainer>
    );
}
